// Aus Tastendrücken und Mausbewegung wird je Tick eine Absicht (Architektur-Regel 3). Dieses
// Modul kennt kein DOM: `index.ts` reicht die Ereignisse herein, `takeIntent` gibt die Absicht
// des Ticks heraus und setzt Flanken und Mausbewegung zurück. Ohne Pointer Lock wird nichts
// verarbeitet; die Freigabe vergisst gehaltene Tasten, damit nach Escape nichts weiterläuft.

import { emptyIntent, type Intent } from '@bauernhof/shared';
import { KEYS } from './keys.js';

/** Drehung je Pixel Mausbewegung im Bogenmaß. */
export const MOUSE_SENSITIVITY = 0.0025;

export interface IntentSource {
  /**
   * Eine Taste geht runter. `repeat` ist `KeyboardEvent.repeat`: Ein wiederholtes Ereignis ist nie
   * eine Flanke – auch dann nicht, wenn die Quelle die gehaltenen Tasten gerade vergessen hat,
   * etwa weil die Maus nach dem Schließen der Kiste neu gefangen wurde (ADR 0031).
   */
  keyDown(code: string, repeat?: boolean): void;
  keyUp(code: string): void;
  /** Mausbewegung in Pixeln; `dy` positiv nach unten wie im Browser. */
  mouseMove(dx: number, dy: number): void;
  setLocked(locked: boolean): void;
  isLocked(): boolean;
  /**
   * Die Absicht dieses Ticks. `interactKey` ist die Taste der verfügbaren Handlung
   * (`KeyboardEvent.code`) oder `undefined`, wenn es nichts zu tun gibt.
   */
  takeIntent(interactKey: string | undefined): Intent;
  /**
   * Ob seit dem letzten Aufruf die Sichttaste gedrückt wurde, und setzt die Flanke zurück. Der
   * Wechsel zwischen Ego-Sicht und Schulterkamera steht bewusst nicht in der Absicht: Er ändert
   * nichts am Spielzustand und geht später auch nicht an den Server (Architektur-Regel 1 und 3).
   */
  takeSichtWechsel(): boolean;
  /** Ob seit dem letzten Aufruf T gedrückt wurde (Satz-Menü, task-016), und setzt die Flanke zurück. */
  takeSatzMenue(): boolean;
  /** Ob Tab gerade gehalten wird (Anwesenheitsliste, task-016). */
  listeGehalten(): boolean;
}

export function createIntentSource(): IntentSource {
  const gehalten = new Set<string>();
  /** Seit dem letzten Tick frisch gedrückt (Flanken). */
  const gedrueckt = new Set<string>();
  let locked = false;
  let mausX = 0;
  let mausY = 0;
  /**
   * Flanken der Tasten ohne Absicht – Sicht (V) und Satz-Menü (T). `takeIntent` räumt `gedrueckt` je Tick
   * weg; diese bleiben, bis main.ts sie abholt.
   */
  const ansichtFlanken = new Set<string>();
  /** Holt eine Flanke ab: wahr genau einmal je Tastendruck. */
  const nimmFlanke = (code: string): boolean => ansichtFlanken.delete(code);

  const alleLoslassen = (): void => {
    gehalten.clear();
    gedrueckt.clear();
    ansichtFlanken.clear();
    mausX = 0;
    mausY = 0;
  };

  return {
    keyDown(code, repeat = false) {
      if (!locked) return;
      // Auto-Repeat des Browsers liefert weitere keydown-Ereignisse; nur der erste ist eine Flanke.
      // `gehalten` allein reicht dafür nicht, weil setLocked es leert, während die Taste noch unten ist.
      if (!repeat && !gehalten.has(code)) {
        gedrueckt.add(code);
        if (code === KEYS.sicht || code === KEYS.saetze) ansichtFlanken.add(code);
      }
      gehalten.add(code);
    },
    keyUp(code) {
      gehalten.delete(code);
    },
    mouseMove(dx, dy) {
      if (!locked) return;
      mausX += dx;
      mausY += dy;
    },
    setLocked(wert) {
      locked = wert;
      alleLoslassen();
    },
    isLocked() {
      return locked;
    },
    takeIntent(interactKey) {
      const absicht = emptyIntent();
      if (!locked) return absicht;
      absicht.forward = richtung(gehalten.has(KEYS.forward), gehalten.has(KEYS.back));
      absicht.strafe = richtung(gehalten.has(KEYS.right), gehalten.has(KEYS.left));
      absicht.jump = gehalten.has(KEYS.jump);
      absicht.up = gehalten.has(KEYS.jump);
      absicht.down = gehalten.has(KEYS.down);
      absicht.toggleFly = gedrueckt.has(KEYS.fly);
      absicht.interact = interactKey !== undefined && gedrueckt.has(interactKey);
      // Maus nach rechts dreht nach rechts; positive yaw dreht laut Vertrag nach links.
      // `0 - x` statt `-x`, damit aus keiner Bewegung +0 wird und nicht -0.
      absicht.lookYaw = (0 - mausX) * MOUSE_SENSITIVITY;
      // Bildschirm-y wächst nach unten; positive pitch blickt nach oben.
      absicht.lookPitch = (0 - mausY) * MOUSE_SENSITIVITY;
      gedrueckt.clear();
      mausX = 0;
      mausY = 0;
      return absicht;
    },
    takeSichtWechsel: () => nimmFlanke(KEYS.sicht),
    takeSatzMenue: () => nimmFlanke(KEYS.saetze),
    listeGehalten() {
      return locked && gehalten.has(KEYS.liste);
    },
  };
}

function richtung(plus: boolean, minus: boolean): -1 | 0 | 1 {
  if (plus === minus) return 0;
  return plus ? 1 : -1;
}
